import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { P } from '@mxenabled/mxui'

import { Loader, useFinstrongStore, useGlobalCopyStore, useGlobalUiStore } from '../../common'
import { LineChart } from '../../common/components/charts/LineChart'

import { calculateFinstrongScoreStatus } from '../util/finstrongUtils'

import { FinancialStrengthGraph } from './FinancialStrengthGraph'
import KeyIndicatorsDrawer from './KeyIndicatorsDrawer'
import ScoreHistoryTimeline from './shared/ScoreHistoryTimeline'

interface ScoreHistoryDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const ScoreHistoryDrawer = ({ isOpen, onClose }: ScoreHistoryDrawerProps) => {
  const theme = useTheme()
  const { finstrong: copy } = useGlobalCopyStore()
  const { isCopyLoaded, isInitialized } = useGlobalUiStore()
  const { augmentedChartData, healthScore, healthScoreChangeReports, peerScore } =
    useFinstrongStore()

  if (!isCopyLoaded || !isInitialized) return <Loader />

  const healthScoreStatus = calculateFinstrongScoreStatus(healthScore.health_score || 0, copy)
  const sortedHealthScoreChangeReports = healthScoreChangeReports.slice().reverse()

  return (
    <KeyIndicatorsDrawer
      additionalContent={
        <Stack
          sx={{
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <LineChart
            axisColor={theme.palette.divider}
            baseline="min"
            colors={[theme.palette.primary.main]}
            curveType="natural"
            datasets={[augmentedChartData]}
            height={160}
            isGraphClippingXAxis={true}
            labels={[copy.key_indicators_label]}
            margin={{ top: 24, bottom: 6, right: 24, left: -17 }}
            peerAverage={Math.round(peerScore?.average_health_score?.average_health_score || 0)}
            showArea={true}
            showAverage={true}
            title={copy.peer_average}
            useCustomMark={true}
          />
        </Stack>
      }
      drawerTitle={copy.score_history}
      isOpen={isOpen}
      loadConnectAccountsCard={false}
      onClose={onClose}
      stickyComponent={
        <Stack
          sx={{
            backgroundColor: 'background.paper',
            pb: 11,
            pt: 8,
            px: 24,
          }}
        >
          <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 16 }}>
            <FinancialStrengthGraph
              arcRadius={24}
              healthScore={healthScore?.health_score}
              margin={{ top: 9 }}
              size="SM"
            />

            <Stack sx={{ gap: 4 }}>
              <P variant="h3">{healthScoreStatus.status}</P>
              <P variant="caption">{healthScoreStatus.description}</P>
            </Stack>
          </Stack>
        </Stack>
      }
    >
      <Stack sx={{ gap: 16 }}>
        {sortedHealthScoreChangeReports.map((report, index) => (
          <ScoreHistoryTimeline key={index} report={report} />
        ))}
      </Stack>
    </KeyIndicatorsDrawer>
  )
}

export default observer(ScoreHistoryDrawer)
